import "./AnamnesePage.scss";
import "react-datepicker/dist/react-datepicker.css";

import { Badge, Col, Row } from "react-bootstrap";
import React, { useEffect, useMemo, useState } from "react";
import {
  faAdd,
  faCalendar,
  faClipboardList,
  faClock,
  faCopy,
  faEraser,
  faEye,
  faFilter,
  faTrash,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { formatDate, formatDateTime } from "../../../../../commons/util/HallAppUtil";

import ActionPillButton from "../../../../../commons/buttons/ActionPillButton";
import AnamneseAPI from "../../api/AnamneseAPI";
import AnamneseModal from "./AnamneseModal";
import AppButton from "../../../../../commons/buttons/AppButton";
import AppInput from "../../../../../commons/inputs/AppInput";
import AppInputSelectV2 from "../../../../../commons/inputs/AppInputSelectV2";
import AppLinkButton from "../../../../../commons/buttons/AppLinkButton";
import AppLoading from "../../../../../commons/AppLoading";
import { Calendar } from "primereact/calendar";
import ConfirmModal from "../../../../../commons/modal/ConfirmModal";
import CustomCard from "../../../../../commons/cards/Card";
import EmptyState from "../../../../../commons/EmptyState";
import FloatingButton from "../../../../../commons/buttons/FloatingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import FormTemplateAPI from "../../api/FormTemplateAPI";
import HallAppToast from "../../../../../commons/HallAppToast";
import MenuTitle from "../../../../../commons/menu/MenuTitle";
import SpaceMenuBottom from "../../../../../commons/SpaceMenuBottom";
import { addLocale } from "primereact/api";
import { format } from "date-fns";
import pt from "date-fns/locale/pt-BR";
import { useNavigate } from "react-router-dom";

addLocale("pt", {
  firstDayOfWeek: 1,
  dayNames: ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"],
  dayNamesShort: ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"],
  dayNamesMin: ["D", "S", "T", "Q", "Q", "S", "S"],
  monthNames: [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ],
  monthNamesShort: ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"],
  today: "Hoje",
  clear: "Limpar",
  dateFormat: "dd/mm/yyyy",
  weekHeader: "Sem",
});

const anamneseAPI = new AnamneseAPI();
const formAPI = new FormTemplateAPI();
const PAGE_SIZE = 50;

export default function AnamnesePage() {
  const [isLoading, setLoading] = useState(true);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [showModalAnamnese, setShowModalAnamnese] = useState(false);

  // listagem paginada
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalElements, setTotalElements] = useState(0);

  // ========= filtros =========
  const [q, setQ] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [templateOptions, setTemplateOptions] = useState([]);
  const [dateType, setDateType] = useState("CREATE_DT"); // CREATE_DT | FILLED_DT

  const today = useMemo(() => new Date(), []);
  const firstDayOfMonth = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
  const [rangeDates, setRangeDates] = useState([firstDayOfMonth, today]);

  const [dateFrom, setDateFrom] = useState(formatDateYMD(firstDayOfMonth));
  const [dateTo, setDateTo] = useState(formatDateYMD(today));

  const [appliedFilters, setAppliedFilters] = useState({
    q: "",
    templateId: "",
    dateType: "CREATE_DT",
    dateFrom: formatDateYMD(firstDayOfMonth),
    dateTo: formatDateYMD(today),
  });

  const [showModalDelete, setShowModalDelete] = useState(false);
  const [isLoadingModalDelete, setIsLoadingModalDelete] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function boot() {
    try {
      setLoading(true);
      await Promise.all([loadTemplatesSafe(), loadData(0, true, appliedFilters)]);
    } finally {
      setLoading(false);
    }
  }

  async function loadTemplatesSafe() {
    try {
      const resp = await formAPI.list?.();
      const list = resp?.data || [];

      const opts = Array.isArray(list)
        ? list.map((t) => ({
            value: String(t.id),
            title: t.name,
            subtitle: t.description || "",
          }))
        : [];

      setTemplateOptions([{ value: "", title: "Todos os formulários", subtitle: "" }, ...opts]);
    } catch (e) {
      setTemplateOptions([{ value: "", title: "Todos os formulários", subtitle: "" }]);
      console.warn("Sem endpoint de templates (list).", e);
    }
  }

  function openDeleteModal(item) {
    setSelectedItem(item);
    setShowModalDelete(true);
  }

  async function executeDeletion() {
    if (!selectedItem?.id) return;

    setIsLoadingModalDelete(true);
    try {
      await anamneseAPI?.delete(selectedItem.id);

      HallAppToast.success("Anamnese excluída!");
      setShowModalDelete(false);
      setSelectedItem(null);

      loadData(0, true, appliedFilters);
    } catch (_) {
      HallAppToast.genericError();
    } finally {
      setIsLoadingModalDelete(false);
    }
  }

  const dateTypeOptions = useMemo(
    () => [
      { value: "CREATE_DT", title: "Data de criação", subtitle: "" },
      { value: "FILLED_DT", title: "Data de preenchimento", subtitle: "" },
    ],
    []
  );

  const statusLabel = useMemo(
    () => (st) => {
      switch (st) {
        case "CREATED":
          return "Criado";
        case "DRAFT":
          return "Rascunho";
        case "SUBMITTED":
        case "COMPLETED":
          return "Completo";
        default:
          return st || "-";
      }
    },
    []
  );

  const statusVariant = useMemo(
    () => (st) => {
      switch (st) {
        case "CREATED":
          return "secondary";
        case "DRAFT":
          return "warning";
        case "SUBMITTED":
        case "COMPLETED":
          return "success";
        default:
          return "dark";
      }
    },
    []
  );

  function buildParams(targetPage, filters) {
    const params = {
      page: targetPage,
      size: PAGE_SIZE,
    };

    if (filters?.q) params.q = filters.q;
    if (filters?.templateId) params.templateId = filters.templateId;
    if (filters?.dateType) params.dateType = filters.dateType;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;

    return params;
  }

  async function loadData(targetPage = 0, replace = false, filters = appliedFilters) {
    try {
      if (replace) setLoading(true);
      else setLoadingMore(true);

      const params = buildParams(targetPage, filters);
      const resp = await anamneseAPI.list(params);

      const data = resp?.data || {};
      const content = Array.isArray(data?.content) ? data.content : [];

      setTotalElements(Number(data?.totalElements || 0));
      setPage(Number(data?.number ?? targetPage));
      setHasMore(!Boolean(data?.last));

      if (replace) setItems(content);
      else setItems((prev) => [...prev, ...content]);
    } catch (e) {
      console.error(e);
      if (replace) {
        setItems([]);
        setTotalElements(0);
        setHasMore(false);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  function applyFilters() {
    const next = {
      q: String(q || "").trim(),
      templateId: String(templateId || "").trim(),
      dateType: String(dateType || "CREATE_DT").trim(),
      dateFrom: String(dateFrom || "").trim(),
      dateTo: String(dateTo || "").trim(),
    };

    setAppliedFilters(next);
    loadData(0, true, next);
  }

  function clearFilters() {
    setQ("");
    setTemplateId("");
    setDateType("CREATE_DT");

    const t = new Date();
    const first = new Date(t.getFullYear(), t.getMonth(), 1);

    setRangeDates([first, t]);
    setDateFrom(formatDateYMD(first));
    setDateTo(formatDateYMD(t));

    const empty = {
      q: "",
      templateId: "",
      dateType: "CREATE_DT",
      dateFrom: formatDateYMD(first),
      dateTo: formatDateYMD(t),
    };

    setAppliedFilters(empty);
    loadData(0, true, empty);
  }

  function openNew() {
    setShowModalAnamnese(true);
  }

  function closeModal() {
    setShowModalAnamnese(false);
    loadData(0, true, appliedFilters);
  }

  function onOpenRow(row) {
    navigate(`/anamnese/${row.id}`);
  }

  function onChangeRange(next) {
    setRangeDates(next);

    const [start, end] = next || [];
    setDateFrom(formatDateYMD(start));
    setDateTo(formatDateYMD(end));
  }

  function RangePickerPrime() {
    return (
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 12,
          border: "1px solid #dee2e6",
          background: "#fff",
        }}
      >
        <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 12, marginBottom: 8 }}>
          <FontAwesomeIcon icon={faCalendar} />
          <span>Período</span>
        </div>

        <Calendar
          maxDate={new Date()}
          locale="pt"
          dateFormat="dd/mm/yy"
          value={rangeDates}
          onChange={(e) => onChangeRange(e.value)}
          selectionMode="range"
          readOnlyInput
          hideOnRangeSelection
          style={{ width: "100%", textAlign: "center", zIndex: 0 }}
        />

        <div className="text-muted mt-2" style={{ fontSize: 11 }}>
          {dateFrom && dateTo ? (
            <>
              Selecionado: <b>{dateFrom}</b> até <b>{dateTo}</b>
            </>
          ) : (
            <>Selecione um intervalo</>
          )}
        </div>
      </div>
    );
  }

  function AnamneseCard({ data }) {
    const hasFilled = !!data?.filledDt;

    const onCopy = () => {
      const link = `https://agendamento.hallapp.com.br/reply/${data.id}`;

      navigator.clipboard
        .writeText(link)
        .then(() => {
          HallAppToast.success("Link copiado para a área de transferência!");
        })
        .catch(() => {
          HallAppToast.genericError("Não foi possível copiar o link.");
        });
    };

    return (
      <CustomCard>
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold" style={{ fontSize: 16, lineHeight: "20px" }}>
              {data.customerName || "—"}
            </div>

            <div className="text-muted" style={{ fontSize: 12, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <FontAwesomeIcon icon={faUser} />
                {data.whatsapp || "—"}
              </span>

              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <FontAwesomeIcon icon={faClipboardList} />
                #{data.id}
              </span>
            </div>
          </div>

          <Badge bg={statusVariant(data.status)} pill>
            {statusLabel(data.status)}
          </Badge>
        </div>

        <hr className="my-3" />

        <div style={{ minWidth: 0 }}>
          <div className="fw-semibold" style={{ fontSize: 14 }}>
            {data.templateName || "—"}
          </div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            {data.description || "Sem descrição"}
          </div>
        </div>

        <hr className="my-3" />

        <div className="d-flex flex-column gap-2">
          <div className="d-flex align-items-center justify-content-between">
            <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
              <FontAwesomeIcon icon={faClock} />
              <span>Criado em</span>
            </div>
            <div className="fw-semibold" style={{ fontSize: 13 }}>
              {data.createDt ? formatDateTime(data.createDt) : "-"}
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
              <FontAwesomeIcon icon={faCalendar} />
              <span>Preenchido</span>
            </div>
            <div className="fw-semibold" style={{ fontSize: 13 }}>
              {hasFilled ? formatDate(data.filledDt) : "—"}
            </div>
          </div>
        </div>

        {/* AÇÕES (ActionPillButton) */}
        <div className="mt-3 d-flex align-items-center justify-content-center">
          <div className="d-flex gap-2 flex-wrap justify-content-center">
            <ActionPillButton
              label="Abrir"
              icon={faEye}
              onClick={() => onOpenRow(data)}
              disabled={false}
              title="Abrir anamnese"
              size="sm"
            />

            <ActionPillButton
              label="Copiar link"
              icon={faCopy}
              onClick={onCopy}
              disabled={false}
              title="Copiar link de resposta"
              size="sm"
              variant="outline"
              color="#111827"
              borderColor="#e8e8f0"
              backgroundColor="#ffffff"
            />

            <ActionPillButton
              label="Excluir"
              icon={faTrash}
              onClick={() => openDeleteModal(data)}
              disabled={false}
              title="Excluir anamnese"
              size="sm"
              variant="outline"
              color="#b91c1c"
              borderColor="#fecaca"
              backgroundColor="#ffffff"
            />
          </div>
        </div>
      </CustomCard>
    );
  }

  const hasApplied =
    !!appliedFilters.q ||
    !!appliedFilters.templateId ||
    !!appliedFilters.dateFrom ||
    !!appliedFilters.dateTo ||
    appliedFilters.dateType !== "CREATE_DT";

  return (
    <>
      <div className="container">
        <MenuTitle title="Anamnese" showArrowInfo={false} />

        {/* ====== Filtros ====== */}
        <div style={{ marginTop: 10 }}>
          <CustomCard>
            <div className="d-flex align-items-center justify-content-between">
              <div className="fw-semibold" style={{ fontSize: 14, display: "flex", alignItems: "center", gap: 8 }}>
                <FontAwesomeIcon icon={faFilter} />
                Filtros
              </div>

              <button className="btn btn-link p-0" style={{ fontSize: 12 }} onClick={clearFilters} type="button">
                <FontAwesomeIcon icon={faEraser} /> Limpar
              </button>
            </div>

            <hr className="my-3" />

            <Row className="g-2">
              <Col xs={12} md={5}>
                <AppInput
                  name="q"
                  type="text"
                  label="Cliente (nome ou WhatsApp)"
                  placeholder="Ex: Joao ou 11999999999"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  errors={{}}
                  touched={{}}
                />

                <div className="text-muted" style={{ fontSize: 11, marginTop: -8 }}>
                  Busca parcial por nome do cliente ou WhatsApp.
                </div>
              </Col>

              <Col xs={12} md={4}>
                <AppInputSelectV2
                  placeholder="Selecione um formulário"
                  options={templateOptions}
                  name="templateId"
                  value={templateId}
                  onChange={(e) => setTemplateId(e.target.value)}
                />

                <div className="text-muted" style={{ fontSize: 11, marginTop: -8 }}>
                  Filtra pela ficha (template).
                </div>
              </Col>

              <Col xs={12} md={3}>
                <AppInputSelectV2
                  placeholder="Tipo de data"
                  options={dateTypeOptions}
                  name="dateType"
                  value={dateType}
                  onChange={(e) => setDateType(e.target.value)}
                />

                <div style={{ marginTop: 10 }}>
                  <RangePickerPrime />
                </div>
              </Col>

              <Col xs={12} className="d-flex gap-2 mt-2">
                <AppButton name="Aplicar filtros" fullWidth={false} onClick={applyFilters} disabled={isLoading} />
                <AppLinkButton
                  name="Recarregar"
                  fullWidth={false}
                  onClick={() => loadData(0, true, appliedFilters)}
                  disabled={isLoading}
                />
              </Col>

              {hasApplied && (
                <Col xs={12}>
                  <hr className="my-3" />
                  <div className="d-flex flex-wrap gap-2">
                    {appliedFilters.q ? (
                      <span
                        className="px-2 py-1 rounded-pill"
                        style={{ fontSize: 12, background: "#f1f3ff", border: "1px solid #e3e6ff" }}
                      >
                        Cliente/WhatsApp: <b>{appliedFilters.q}</b>
                      </span>
                    ) : null}

                    {appliedFilters.dateFrom || appliedFilters.dateTo ? (
                      <span
                        className="px-2 py-1 rounded-pill"
                        style={{ fontSize: 12, background: "#f1f3ff", border: "1px solid #e3e6ff" }}
                      >
                        {appliedFilters.dateType === "FILLED_DT" ? "Preenchimento" : "Criação"}:{" "}
                        <b>{formatDate(appliedFilters.dateFrom) || "—"}</b> até{" "}
                        <b>{formatDate(appliedFilters.dateTo) || "—"}</b>
                      </span>
                    ) : null}
                  </div>
                </Col>
              )}
            </Row>
          </CustomCard>
        </div>

        {/* ====== Lista ====== */}
        <div style={{ marginTop: 12 }}>
          {isLoading ? (
            <AppLoading />
          ) : (
            <Row className="g-3" style={{ margin: 0, width: "100%" }}>
              {items.length === 0 ? (
                <EmptyState
                  title="Nenhuma anamnese encontrada"
                  description="Ajuste os filtros ou crie uma nova anamnese."
                  icon={<FontAwesomeIcon icon={faClipboardList} />}
                  actionLabel="Criar anamnese"
                  onAction={openNew}
                />
              ) : (
                <>
                  <Col xs={12} style={{ paddingLeft: 0, paddingRight: 0 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Total: <b>{totalElements}</b>
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Página: <b>{page + 1}</b>
                      </div>
                    </div>
                  </Col>

                  {items.map((data) => (
                    <Col xs={12} sm={6} md={4} lg={4} key={data.id} className="mb-3">
                      <div style={{ width: "100%" }}>
                        <AnamneseCard data={data} />
                      </div>
                    </Col>
                  ))}

                  {hasMore && (
                    <Col xs={12} style={{ display: "flex", justifyContent: "center", padding: "6px 0 30px" }}>
                      <ActionPillButton
                        label={isLoadingMore ? "Carregando..." : "Carregar mais"}
                        icon={faCalendar}
                        onClick={() => loadData(page + 1, false, appliedFilters)}
                        disabled={isLoadingMore}
                        title="Carregar mais"
                        size="md"
                        variant="outline"
                      />
                    </Col>
                  )}

                  <div style={{ height: "calc(50px + 72px)" }} />
                </>
              )}
            </Row>
          )}
        </div>
      </div>

      {showModalDelete && (
        <ConfirmModal
          show={showModalDelete}
          title="Excluir"
          isLoading={isLoadingModalDelete}
          confirm={executeDeletion}
          closeModal={() => {
            setShowModalDelete(false);
            setSelectedItem(null);
          }}
        >
          Deseja excluir a anamnese do(a) cliente{" "}
          <span style={{ fontWeight: "bold" }}>{selectedItem?.customerName || "-"}</span>?
        </ConfirmModal>
      )}

      <SpaceMenuBottom />

      <FloatingButton icon={faAdd} onClick={openNew} />

      {showModalAnamnese && <AnamneseModal show={showModalAnamnese} closeShow={closeModal} />}
    </>
  );
}

function formatDateYMD(date) {
  if (!date) return "";
  try {
    return format(date, "yyyy-MM-dd", { locale: pt });
  } catch {
    return "";
  }
}